import React from "react";
import {
    PGEntriesTable,
    PGEntriesHeader,
    PGEntriesTitle,
    PGTableWrap,
    PGTable,
    PGCellWarn,
    PGEditBtn,
    PGPrimaryBtn,
} from "./PartographStyles";

/* ═══════════════════════════════════════════════════════════════
   PartographEntries — Tabular view of all partograph entries
   
   Shows all recorded observations in a horizontally scrollable
   table with:
     - Time, dilation, descent, FHR, contractions
     - Amniotic fluid, moulding, maternal vitals
     - Abnormal values highlighted in red
     - Edit button per row
   ═══════════════════════════════════════════════════════════════ */

const formatTime = function(dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        const pad = function(n) { return n < 10 ? "0" + n : "" + n; };
        return pad(d.getHours()) + ":" + pad(d.getMinutes());
    } catch (e) {
        return "—";
    }
};

const fluidLabel = function(val) {
    if (!val) return "—";
    const map = {
        clear: "C",
        meconium_1: "M1",
        meconium_2: "M2",
        meconium_3: "M3",
        blood: "B",
        absent: "A",
    };
    return map[val] || val;
};

const mouldLabel = function(val) {
    if (val == null || val === "") return "—";
    const map = { "0": "0", "1": "+", "2": "++", "3": "+++" };
    return map[String(val)] || val;
};


export const PartographEntries = function(props) {
    const pg = props.pg;
    const isCompleted = props.isCompleted || false;
    const entries = pg.entries || [];

    if (!entries.length) {
        return React.createElement("div", {
            style: {
                textAlign: "center",
                padding: "40px 16px",
                color: "#90a4ae",
                fontSize: "13px",
            },
        }, "No entries recorded yet. Switch to ", React.createElement("strong", null, "Record Entry"), " tab to add observations.");
    }

    return React.createElement(PGEntriesTable, null,
        React.createElement(PGEntriesHeader, null,
            React.createElement(PGEntriesTitle, null,
                "📋 All Entries (",
                entries.length,
                ")"
            ),
            !isCompleted ? React.createElement(PGPrimaryBtn, {
                onClick: function() { pg.setActiveTab("entry"); },
                style: { padding: "5px 14px", fontSize: "11px" },
            }, "+ New Entry") : null
        ),
        React.createElement(PGTableWrap, null,
            React.createElement(PGTable, null,
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", null, "Time"),
                        React.createElement("th", null, "Hours"),
                        React.createElement("th", null, "Dilation"),
                        React.createElement("th", null, "Descent"),
                        React.createElement("th", null, "FHR"),
                        React.createElement("th", null, "Contr."),
                        React.createElement("th", null, "Dur."),
                        React.createElement("th", null, "AMF"),
                        React.createElement("th", null, "Mould"),
                        React.createElement("th", null, "BP"),
                        React.createElement("th", null, "Pulse"),
                        React.createElement("th", null, "Temp"),
                        React.createElement("th", null, "Oxytocin"),
                        !isCompleted ? React.createElement("th", null, "") : null
                    )
                ),
                React.createElement("tbody", null,
                    entries.map(function(entry, idx) {
                        const fhr = parseFloat(entry.fetal_heart_rate);
                        const fhrBad = fhr && (fhr < 110 || fhr > 160);
                        const bpSys = parseFloat(entry.maternal_bp_systolic);
                        const bpDia = parseFloat(entry.maternal_bp_diastolic);
                        const bpBad = (bpSys && bpSys >= 140) || (bpDia && bpDia >= 90);
                        const tempVal = parseFloat(entry.maternal_temperature);
                        const tempBad = tempVal && tempVal >= 38;
                        const mouldVal = parseInt(entry.moulding);
                        const mouldBad = mouldVal >= 2;
                        const fluidVal = entry.amniotic_fluid || "";
                        const fluidBad = fluidVal.indexOf("meconium") >= 0;

                        return React.createElement("tr", { key: entry.id || idx },
                            React.createElement("td", null, formatTime(entry.recorded_at)),
                            React.createElement("td", { style: { fontWeight: 600 } },
                                (parseFloat(entry.hours_since_start) || 0).toFixed(1) + "h"
                            ),
                            React.createElement("td", { style: { fontWeight: 700, color: "#004d40" } },
                                entry.cervical_dilation_cm != null && entry.cervical_dilation_cm !== "" ? entry.cervical_dilation_cm + " cm" : "—"
                            ),
                            React.createElement("td", { style: { color: "#e65100" } },
                                entry.descent_fifths != null && entry.descent_fifths !== "" ? entry.descent_fifths + "/5" : "—"
                            ),
                            React.createElement("td", null,
                                React.createElement(PGCellWarn, { warn: fhrBad },
                                    fhr ? fhr + " bpm" : "—"
                                )
                            ),
                            React.createElement("td", null,
                                entry.contractions_per_10min || "—"
                            ),
                            React.createElement("td", null,
                                entry.contraction_duration_seconds ? entry.contraction_duration_seconds + "s" : "—"
                            ),
                            React.createElement("td", null,
                                React.createElement(PGCellWarn, { warn: fluidBad },
                                    fluidLabel(entry.amniotic_fluid)
                                )
                            ),
                            React.createElement("td", null,
                                React.createElement(PGCellWarn, { warn: mouldBad },
                                    mouldLabel(entry.moulding)
                                )
                            ),
                            React.createElement("td", null,
                                React.createElement(PGCellWarn, { warn: bpBad },
                                    bpSys ? bpSys + "/" + (bpDia || "—") : "—"
                                )
                            ),
                            React.createElement("td", null,
                                entry.maternal_pulse || "—"
                            ),
                            React.createElement("td", null,
                                React.createElement(PGCellWarn, { warn: tempBad },
                                    tempVal ? tempVal + "°C" : "—"
                                )
                            ),
                            React.createElement("td", null,
                                entry.oxytocin_drops_per_min ? entry.oxytocin_drops_per_min + " d/m" : "—"
                            ),
                            !isCompleted ? React.createElement("td", null,
                                React.createElement(PGEditBtn, {
                                    onClick: function() { pg.startEditEntry(entry); },
                                }, "Edit")
                            ) : null
                        );
                    })
                )
            )
        )
    );
};
